import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search, Star, Briefcase, Users, ArrowRight, MapPin, Clock,
  IndianRupee, FileText, ChevronLeft, ChevronRight
} from 'lucide-react';
import Footer from '../components/Footer';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExplorePage = () => {
  const navigate = useNavigate();
  const [gigs, setGigs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [gigPage, setGigPage] = useState(1);
  const [projPage, setProjPage] = useState(1);
  const [flPage, setFlPage] = useState(1);
  const [gigTotal, setGigTotal] = useState(0);
  const [projTotal, setProjTotal] = useState(0);
  const [flTotal, setFlTotal] = useState(0);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [g, p, f] = await Promise.all([
        axios.get(`${API}/public/gigs?page_size=6`),
        axios.get(`${API}/public/projects?page_size=6`),
        axios.get(`${API}/public/freelancers?page_size=6`)
      ]);
      setGigs(g.data.items); setGigTotal(g.data.total);
      setProjects(p.data.items); setProjTotal(p.data.total);
      setFreelancers(f.data.items); setFlTotal(f.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const searchAll = async () => {
    setLoading(true);
    try {
      const [g, p, f] = await Promise.all([
        axios.get(`${API}/public/gigs?search=${search}&page_size=6`),
        axios.get(`${API}/public/projects?search=${search}&page_size=6`),
        axios.get(`${API}/public/freelancers?search=${search}&page_size=6`)
      ]);
      setGigs(g.data.items); setGigTotal(g.data.total);
      setProjects(p.data.items); setProjTotal(p.data.total);
      setFreelancers(f.data.items); setFlTotal(f.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <a href="/" className="font-heading font-bold text-xl text-primary" data-testid="explore-logo">TaskPiece</a>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="explore-login-btn">Log In</Button>
            <Button onClick={() => navigate('/signup')} data-testid="explore-signup-btn">Sign Up</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-3" data-testid="explore-title">
            Explore the Marketplace
          </h1>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto mb-6">
            Browse gigs, projects, and talented freelancers. Sign up to start hiring or working.
          </p>
          <div className="max-w-lg mx-auto flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search gigs, projects, skills..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchAll()}
                className="pl-10 h-12"
                data-testid="explore-search"
              />
            </div>
            <Button onClick={searchAll} className="h-12 px-6" data-testid="explore-search-btn">Search</Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">Loading marketplace...</div>
        ) : (
          <Tabs defaultValue="gigs">
            <TabsList className="mb-6">
              <TabsTrigger value="gigs" data-testid="explore-gigs-tab">Gigs ({gigTotal})</TabsTrigger>
              <TabsTrigger value="projects" data-testid="explore-projects-tab">Projects ({projTotal})</TabsTrigger>
              <TabsTrigger value="freelancers" data-testid="explore-freelancers-tab">Freelancers ({flTotal})</TabsTrigger>
            </TabsList>

            {/* GIGS */}
            <TabsContent value="gigs">
              {gigs.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No gigs found</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gigs.map(g => (
                    <Card key={g.id} className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate(`/explore/gig/${g.id}`)} data-testid={`explore-gig-${g.id}`}>
                      <CardContent className="pt-5 pb-5">
                        <div className="flex items-center gap-2 mb-2">
                          {g.task_code && <code className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-mono">{g.task_code}</code>}
                          <Badge variant="outline" className="text-xs">{g.category}</Badge>
                        </div>
                        <h3 className="font-heading font-bold text-lg mb-1 group-hover:text-secondary transition-colors">{g.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{g.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="font-heading font-bold text-secondary text-lg">
                            ₹{g.price?.toLocaleString('en-IN')}
                          </span>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> {g.rating?.toFixed(1)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                          <span>by {g.freelancer_name}</span>
                          <span>·</span>
                          <Clock className="w-3 h-3" /> {g.delivery_days}d delivery
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              <div className="text-center mt-8">
                <Button variant="outline" onClick={() => navigate('/signup')} data-testid="gig-cta">
                  Sign Up to Order a Gig <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            {/* PROJECTS */}
            <TabsContent value="projects">
              {projects.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No open projects</p>
              ) : (
                <div className="space-y-4">
                  {projects.map(p => (
                    <Card key={p.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/explore/project/${p.id}`)} data-testid={`explore-project-${p.id}`}>
                      <CardContent className="pt-5 pb-5">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {p.task_code && <code className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-mono">{p.task_code}</code>}
                              <h3 className="font-heading font-bold text-lg">{p.title}</h3>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{p.description}</p>
                            <div className="flex gap-1.5 flex-wrap">
                              {(p.skills_required || []).map((s, i) => <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>)}
                            </div>
                          </div>
                          <div className="text-right ml-4 flex-shrink-0">
                            <div className="font-heading font-bold text-secondary">
                              ₹{p.budget_min?.toLocaleString('en-IN')} - ₹{p.budget_max?.toLocaleString('en-IN')}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">{p.bids_count} bids</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              <div className="text-center mt-8">
                <Button variant="outline" onClick={() => navigate('/signup')} data-testid="project-cta">
                  Sign Up to Bid on Projects <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            {/* FREELANCERS */}
            <TabsContent value="freelancers">
              {freelancers.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No freelancers found</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {freelancers.map(f => (
                    <Card key={f.id} className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate(`/explore/freelancer/${f.id}`)} data-testid={`explore-fl-${f.id}`}>
                      <CardContent className="pt-5 pb-5">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                            {f.full_name?.charAt(0)}
                          </div>
                          <div>
                            <h3 className="font-heading font-bold group-hover:text-secondary transition-colors">{f.full_name}</h3>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                              <span>{f.rating?.toFixed(1)}</span>
                              <span>({f.total_reviews} reviews)</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{f.bio || 'Freelancer on TaskPiece'}</p>
                        <div className="flex gap-1.5 flex-wrap">
                          {(f.skills || []).slice(0, 4).map((s, i) => <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>)}
                          {(f.skills || []).length > 4 && <Badge variant="outline" className="text-xs">+{f.skills.length - 4}</Badge>}
                        </div>
                        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                          <Briefcase className="w-3 h-3" /> {f.completed_jobs || 0} jobs completed
                          {f.verified && <Badge variant="outline" className="text-[10px] py-0">Verified</Badge>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              <div className="text-center mt-8">
                <Button variant="outline" onClick={() => navigate('/signup')} data-testid="freelancer-cta">
                  Sign Up to Hire Freelancers <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ExplorePage;
